import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Card,
  HStack,
  VStack,
  Badge,
  Checkbox,
  Icon,
} from '@chakra-ui/react'
import { CheckCircle, AlertCircle, Clock } from 'lucide-react'
import { Field } from '@/components/ui/field'
import { NativeSelectRoot, NativeSelectField } from '@/components/ui/native-select'
import { useCreateFundCall, useFiscalYears, useFundCalls, useCurrentFiscalYear, useBudgetCallSuggestions } from '../hooks'
import type { BudgetCallSuggestion } from '../hooks/useBudgets'
import { accountingToaster as toaster } from '../pages/AccountingLayout'
import { BUDGET_CALL_FREQUENCY_LABELS, BUDGET_TYPE_LABELS } from '../types'

interface FundCallCreateModalProps {
  isOpen: boolean
  onClose: () => void
  residenceId: string
}

interface SelectedBudget {
  budget_id: string
  amount: number
  suggestion: BudgetCallSuggestion
}

export function FundCallCreateModal({
  isOpen,
  onClose,
  residenceId,
}: FundCallCreateModalProps) {
  const { data: fiscalYears } = useFiscalYears(residenceId)
  const { data: currentFiscalYear } = useCurrentFiscalYear(residenceId)
  const [selectedFiscalYear, setSelectedFiscalYear] = useState('')
  const { data: budgetSuggestions } = useBudgetCallSuggestions(residenceId, selectedFiscalYear || undefined)
  const { data: existingFundCalls } = useFundCalls(residenceId, selectedFiscalYear || undefined)
  const createFundCall = useCreateFundCall()

  const [formData, setFormData] = useState({
    label: '',
    fiscal_year_id: '',
    call_date: new Date().toISOString().split('T')[0]!,
    due_date: '',
    description: '',
  })

  const [selectedBudgets, setSelectedBudgets] = useState<SelectedBudget[]>([])

  // Calculer le prochain numéro d'appel de fonds
  const nextCallNumber = useMemo(() => {
    if (!existingFundCalls || existingFundCalls.length === 0) return 1
    const maxNumber = Math.max(...existingFundCalls.map((fc) => fc.call_number))
    return maxNumber + 1
  }, [existingFundCalls])

  // Initialiser l'exercice par défaut quand les données sont chargées
  useEffect(() => {
    if (currentFiscalYear && !selectedFiscalYear) {
      setSelectedFiscalYear(currentFiscalYear.id)
      setFormData((prev) => ({ ...prev, fiscal_year_id: currentFiscalYear.id }))
    }
  }, [currentFiscalYear, selectedFiscalYear])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value)
  }

  const getBudgetTypeLabel = (type: string) => {
    return BUDGET_TYPE_LABELS[type as keyof typeof BUDGET_TYPE_LABELS] || type
  }

  const getCallStatusIcon = (status: BudgetCallSuggestion['call_status']) => {
    switch (status) {
      case 'completed':
        return <Icon as={CheckCircle} color="green.500" boxSize={4} />
      case 'in_progress':
        return <Icon as={Clock} color="orange.500" boxSize={4} />
      case 'not_started':
        return <Icon as={AlertCircle} color="blue.500" boxSize={4} />
    }
  }

  const getCallStatusColor = (status: BudgetCallSuggestion['call_status']) => {
    switch (status) {
      case 'completed':
        return 'green'
      case 'in_progress':
        return 'orange'
      case 'not_started':
        return 'blue'
    }
  }

  const handleBudgetToggle = (suggestion: BudgetCallSuggestion, checked: boolean) => {
    if (checked) {
      setSelectedBudgets((prev) => [
        ...prev,
        {
          budget_id: suggestion.budget_id,
          amount: suggestion.suggested_amount,
          suggestion,
        },
      ])
    } else {
      setSelectedBudgets((prev) => prev.filter((b) => b.budget_id !== suggestion.budget_id))
    }
  }

  const handleBudgetAmountChange = (budgetId: string, amount: number) => {
    setSelectedBudgets((prev) =>
      prev.map((b) => (b.budget_id === budgetId ? { ...b, amount } : b))
    )
  }

  const totalAmount = useMemo(() => {
    return selectedBudgets.reduce((sum, b) => sum + b.amount, 0)
  }, [selectedBudgets])

  const handleSubmit = async () => {
    if (!formData.label || !formData.fiscal_year_id || !formData.call_date || !formData.due_date) {
      toaster.create({
        title: 'Erreur',
        description: 'Veuillez remplir tous les champs obligatoires',
        type: 'error',
      })
      return
    }

    if (selectedBudgets.length === 0) {
      toaster.create({
        title: 'Erreur',
        description: 'Veuillez sélectionner au moins un budget',
        type: 'error',
      })
      return
    }

    try {
      await createFundCall.mutateAsync({
        fundCall: {
          residence_id: residenceId,
          label: formData.label,
          call_number: nextCallNumber,
          fiscal_year_id: formData.fiscal_year_id,
          call_date: formData.call_date,
          due_date: formData.due_date,
          description: formData.description || null,
          status: 'draft',
          total_amount: totalAmount,
          sent_at: null,
          sent_by: null,
          created_by: null,
        },
        budgets: selectedBudgets.map((b) => ({
          budget_id: b.budget_id,
          amount: b.amount,
        })),
      })

      toaster.create({
        title: 'Succès',
        description: 'Appel de fonds créé avec succès',
        type: 'success',
      })
      handleClose()
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Erreur lors de la création',
        type: 'error',
      })
    }
  }

  const handleClose = () => {
    setFormData({
      label: '',
      fiscal_year_id: currentFiscalYear?.id || '',
      call_date: new Date().toISOString().split('T')[0]!,
      due_date: '',
      description: '',
    })
    setSelectedBudgets([])
    setSelectedFiscalYear(currentFiscalYear?.id || '')
    onClose()
  }

  const handleFiscalYearChange = (fiscalYearId: string) => {
    setFormData({ ...formData, fiscal_year_id: fiscalYearId })
    setSelectedFiscalYear(fiscalYearId)
    setSelectedBudgets([]) // Reset selected budgets when fiscal year changes
  }

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()} size="lg">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="700px">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>Créer un appel de fonds</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Flex direction="column" gap={4}>
              <Field label="Libellé de l'appel" required>
                <Input
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  placeholder="Ex: Appel de fonds T1 2025"
                />
              </Field>

              <Field label="Exercice comptable" required>
                <NativeSelectRoot>
                  <NativeSelectField
                    value={formData.fiscal_year_id}
                    onChange={(e) => handleFiscalYearChange(e.target.value)}
                  >
                    <option value="">Sélectionner un exercice</option>
                    {fiscalYears?.map((fy) => (
                      <option key={fy.id} value={fy.id}>
                        {fy.name}
                      </option>
                    ))}
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>

              <Field label="Budgets associés" required>
                {!selectedFiscalYear ? (
                  <Text fontSize="sm" color="gray.500">
                    Sélectionnez d'abord un exercice comptable
                  </Text>
                ) : !budgetSuggestions?.length ? (
                  <Text fontSize="sm" color="gray.500">
                    Aucun budget voté ou actif pour cet exercice
                  </Text>
                ) : (
                  <VStack align="stretch" gap={2}>
                    {budgetSuggestions.map((suggestion) => {
                      const isSelected = selectedBudgets.some((b) => b.budget_id === suggestion.budget_id)
                      const selectedBudget = selectedBudgets.find((b) => b.budget_id === suggestion.budget_id)
                      const isCompleted = suggestion.call_status === 'completed'

                      return (
                        <Card.Root
                          key={suggestion.budget_id}
                          size="sm"
                          variant={isSelected ? 'elevated' : 'outline'}
                          borderColor={isSelected ? 'brand.500' : isCompleted ? 'green.200' : 'gray.200'}
                          bg={isSelected ? 'brand.50' : isCompleted ? 'green.50' : 'white'}
                          opacity={isCompleted ? 0.7 : 1}
                        >
                          <Card.Body py={3} px={4}>
                            <Flex align="center" gap={3}>
                              <Checkbox.Root
                                checked={isSelected}
                                onCheckedChange={(e) =>
                                  handleBudgetToggle(suggestion, !!e.checked)
                                }
                                disabled={isCompleted}
                              >
                                <Checkbox.HiddenInput />
                                <Checkbox.Control />
                              </Checkbox.Root>
                              <Box flex={1}>
                                <HStack gap={2} mb={1}>
                                  <Text fontWeight="medium" fontSize="sm">
                                    {suggestion.budget_name}
                                  </Text>
                                  <Badge size="sm" variant="subtle">
                                    {getBudgetTypeLabel(suggestion.budget_type)}
                                  </Badge>
                                  <Badge size="sm" variant="outline" colorPalette={getCallStatusColor(suggestion.call_status)}>
                                    {getCallStatusIcon(suggestion.call_status)}
                                    <Text ml={1}>{suggestion.calls_done}/{suggestion.calls_per_year}</Text>
                                  </Badge>
                                </HStack>
                                <HStack gap={4} fontSize="xs" color="gray.500">
                                  <Text>
                                    Budget: {formatCurrency(suggestion.total_amount)}
                                  </Text>
                                  <Text>
                                    {BUDGET_CALL_FREQUENCY_LABELS[suggestion.call_frequency]}
                                  </Text>
                                  {!isCompleted && (
                                    <Text fontWeight="medium" color="blue.600">
                                      Suggéré: {formatCurrency(suggestion.suggested_amount)}
                                    </Text>
                                  )}
                                </HStack>
                              </Box>
                              {isSelected && (
                                <Box w="150px">
                                  <Input
                                    size="sm"
                                    type="number"
                                    step="0.01"
                                    value={selectedBudget?.amount || 0}
                                    onChange={(e) =>
                                      handleBudgetAmountChange(
                                        suggestion.budget_id,
                                        parseFloat(e.target.value) || 0
                                      )
                                    }
                                  />
                                </Box>
                              )}
                            </Flex>
                          </Card.Body>
                        </Card.Root>
                      )
                    })}

                    {selectedBudgets.length > 0 && (
                      <Card.Root size="sm" bg="gray.100">
                        <Card.Body py={2} px={4}>
                          <Flex justify="space-between" align="center">
                            <Text fontWeight="semibold" fontSize="sm">
                              Montant total de l'appel
                            </Text>
                            <Text fontWeight="bold" fontSize="lg" color="brand.600">
                              {formatCurrency(totalAmount)}
                            </Text>
                          </Flex>
                        </Card.Body>
                      </Card.Root>
                    )}
                  </VStack>
                )}
              </Field>

              <Flex gap={4}>
                <Box flex={1}>
                  <Field label="Date d'appel" required>
                    <Input
                      type="date"
                      value={formData.call_date}
                      onChange={(e) => setFormData({ ...formData, call_date: e.target.value })}
                    />
                  </Field>
                </Box>
                <Box flex={1}>
                  <Field label="Date d'échéance" required>
                    <Input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    />
                  </Field>
                </Box>
              </Flex>

              <Field label="Description">
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description optionnelle"
                />
              </Field>

              <Box p={3} bg="blue.50" borderRadius="md">
                <Text fontSize="sm" color="blue.700">
                  L'appel de fonds sera créé en brouillon. Vous pourrez générer les lignes
                  par copropriétaire selon les clés de répartition avant de l'envoyer.
                </Text>
              </Box>
            </Flex>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={handleClose}>
              Annuler
            </Button>
            <Button
              colorPalette="brand"
              onClick={handleSubmit}
              loading={createFundCall.isPending}
              disabled={selectedBudgets.length === 0}
            >
              Créer l'appel de fonds
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
